import { FC, useState } from 'react'
import { useLocalizedRouter } from '~/components/LocalizedLink/LocalizedLink'
import styles from '~/components/ProductPageLayout/ProductPageLayout.module.css'
import { CustomButton } from '~/components/CustomButton/CustomButton'
import { IconButton, Tooltip } from '@mui/material'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { Modal } from '~/components/Modal/Modal'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQuote, useQuoteResult } from '~/context/pricingQuoteContext'
import type { PricingQuoteResult } from '~/utils/pricingQuote'
import { useServiceOptions } from '~/context/serviceOptionsContext'
import { buildWhatsappUrl } from '~/config/contact'

export type ProductPriceComponent = {
  type: 'price'
  price: number
  predefinedValue?: number
}
interface ProductPriceProps {
  configuration: ProductPriceComponent
  predefinedValue?: number
  onAddItem: (price: number, quote?: PricingQuoteResult) => void
  productName?: string
}
export const ProductPrice: FC<ProductPriceProps> = ({
  configuration,
  onAddItem,
  predefinedValue,
  productName = '',
}) => {
  const router = useLocalizedRouter()
  const intl = useIntl()
  const quote = useQuote()
  const quoteResult = useQuoteResult()

  const [isModalOpen, setIsModalOpen] = useState(false)
  // Delivery & assembly are opt-in: the backend total already includes them, so
  // they are subtracted by default and added back when the user ticks the box.
  // The toggles themselves live in <ServiceOptions> (controls area); state is
  // shared via context.
  const { delivery: deliverySelected, assembly: assemblySelected } =
    useServiceOptions()

  const amountOf = (groupSku: string): number =>
    quote?.breakdown.find((b) => b.groupSku === groupSku)?.amount ?? 0
  const deliveryAmount = amountOf('delivery')
  const assemblyAmount = amountOf('assembly')

  // Quote-driven products (wardrobe(s), stand, bedside) wrap the tree in a
  // QuoteProvider, so `quote` is non-undefined: the live total is the only
  // price source. Other products have no provider (undefined) and keep their
  // static estimate.
  const isQuoteProduct = quote !== undefined
  const price: number | null = isQuoteProduct
    ? quote
      ? Math.round(
          quote.total -
            deliveryAmount -
            assemblyAmount +
            (deliverySelected ? deliveryAmount : 0) +
            (assemblySelected ? assemblyAmount : 0)
        )
      : null // quote pending
    : predefinedValue ?? configuration.price
  const priceReady = price !== null

  const breakdownTooltip = quote ? (
    <div className={styles.priceBreakdown}>
      {quote.breakdown
        .filter((item) => Math.round(item.amount) > 0)
        // delivery/assembly are shown only when selected (opt-in services)
        .filter((item) =>
          item.groupSku === 'delivery'
            ? deliverySelected
            : item.groupSku === 'assembly'
              ? assemblySelected
              : true
        )
        .map((item) => (
        <div key={item.groupId} className={styles.priceBreakdownRow}>
          <span>
            {intl.formatMessage({
              id: `pricing.breakdown.${item.groupSku}`,
              defaultMessage: item.groupName,
            })}
          </span>
          <span>
            {Math.round(item.amount)}&nbsp;
            <FormattedMessage id="homepage.configurator.price.currencyLei" />
          </span>
        </div>
      ))}
      <div className={styles.priceBreakdownTotal}>
        <span>
          <FormattedMessage id="pricing.breakdown.total" />
        </span>
        <span>
          {price}&nbsp;
          <FormattedMessage id="homepage.configurator.price.currencyLei" />
        </span>
      </div>
    </div>
  ) : null

  const resolvedName = productName
    ? intl.formatMessage({ id: productName, defaultMessage: productName })
    : ''
  const productSummary = `${resolvedName} — ${price} lei`

  const openWhatsapp = () => {
    if (!priceReady) return
    const pageUrl = typeof window !== 'undefined' ? window.location.href : ''
    const text = intl.formatMessage(
      {
        id: 'homepage.whatsapp.prefill',
        defaultMessage: 'Bună ziua! Aș dori să comand: {summary}',
      },
      { summary: productSummary }
    )
    window.open(
      buildWhatsappUrl(pageUrl ? `${text}\n${pageUrl}` : text),
      '_blank',
      'noopener,noreferrer'
    )
  }

  return (
    <div>
      <div className={styles.priceRow}>
        {/* data-price-block: promo popup measures dwell time on the price */}
        <div className={styles.priceBox} data-price-block>
          <p className={styles.newPrice}>{priceReady ? price : '…'}</p>
          <p className={styles.newPrice}>
            &nbsp;
            <FormattedMessage id="homepage.configurator.price.currencyLei" />
          </p>
          {breakdownTooltip && (
            <Tooltip
              title={breakdownTooltip}
              arrow
              enterTouchDelay={0}
              leaveTouchDelay={6000}
            >
              <HelpOutlineIcon
                color="action"
                sx={{ fontSize: 20, ml: 0.5, cursor: 'help' }}
                aria-label={intl.formatMessage({
                  id: 'pricing.breakdown.title',
                  defaultMessage: 'Detalierea prețului',
                })}
              />
            </Tooltip>
          )}
        </div>

        <div className={styles.addToCartButtonContainer}>
          <div className={styles.cartButtonWrapper}>
            <CustomButton
              icon={<WhatsAppIcon />}
              size="medium"
              variant="primary"
              outlined
              disabled={!priceReady}
              onClick={openWhatsapp}
            >
              <FormattedMessage id="homepage.button.orderWhatsapp" />
            </CustomButton>
          </div>
          <div className={styles.cartButtonWrapper}>
            <IconButton
              disabled={!priceReady}
              onClick={() => {
                if (!priceReady) return
                setIsModalOpen(true)
                onAddItem(price, quoteResult ?? undefined)
              }}
              className={styles.cartIconButton}
            >
              <ShoppingCartIcon />
            </IconButton>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
        }}
      >
        <h3>
          <FormattedMessage id="modal.addedToCart.title" />
        </h3>
        <div className={styles.buttonRow}>
          <CustomButton onClick={() => router.push('/cart')}>
            <FormattedMessage id="homepage.button.openCart" />
          </CustomButton>
          <CustomButton variant="grey" onClick={() => router.push('/')}>
            <FormattedMessage id="homepage.button.backToPurchase" />
          </CustomButton>
        </div>
      </Modal>
    </div>
  )
}
